import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchDepartmentsPaged,
  fetchDepartmentRow,
  Department,
  DepartmentsPagination,
} from "services/departments.service";
import { AppDispatch, RootState } from "configureStore";

export const DEPARTMENTS_PAGE_SIZE = 20;

interface DepartmentsContainerState {
  isLoading: boolean;
  isLoadingMore: boolean;
  isError: boolean;
  errMsg: string;
  collection: Department[];
  pagination: DepartmentsPagination | null;
  filtersValue: { textSearch?: string };
  // Bumped on every fresh page-1 load; load-more captures it and discards its
  // response if it changed mid-flight (stale-response guard).
  requestId: number;
}

const initialState: DepartmentsContainerState = {
  isLoading: true,
  isLoadingMore: false,
  isError: false,
  errMsg: "",
  collection: [],
  pagination: null,
  filtersValue: {},
  requestId: 0,
};

const normalize = (
  data: any
): { collection: Department[]; pagination: DepartmentsPagination | null } => {
  if (Array.isArray(data)) {
    return { collection: data, pagination: null };
  }
  return {
    collection: data?.collection || [],
    pagination: data?.pagination || null,
  };
};

const departmentsContainerSlice = createSlice({
  name: "departments",
  initialState,
  /* eslint-disable no-param-reassign */
  reducers: {
    setDestroy() {
      return initialState;
    },
    setLoading(
      state: DepartmentsContainerState,
      action: PayloadAction<boolean>
    ) {
      state.isLoading = action.payload;
      if (action.payload) {
        state.isError = false;
        state.errMsg = "";
        state.requestId += 1;
      }
    },
    setLoadingMore(
      state: DepartmentsContainerState,
      action: PayloadAction<boolean>
    ) {
      state.isLoadingMore = action.payload;
    },
    setError(
      state: DepartmentsContainerState,
      action: PayloadAction<{ error: string }>
    ) {
      state.isLoading = false;
      state.isLoadingMore = false;
      state.isError = true;
      state.errMsg = action.payload.error;
    },
    setData(
      state: DepartmentsContainerState,
      action: PayloadAction<{
        collection: Department[];
        pagination: DepartmentsPagination | null;
      }>
    ) {
      state.collection = action.payload.collection;
      state.pagination = action.payload.pagination;
      state.isLoading = false;
      state.isLoadingMore = false;
    },
    appendData(
      state: DepartmentsContainerState,
      action: PayloadAction<{
        collection: Department[];
        pagination: DepartmentsPagination | null;
      }>
    ) {
      const seen = new Set(state.collection.map((d) => d.id));
      const fresh = action.payload.collection.filter((d) => !seen.has(d.id));
      state.collection = [...state.collection, ...fresh];
      state.pagination = action.payload.pagination;
      state.isLoadingMore = false;
    },
    // Replace a single already-loaded department in place (after an inline edit)
    // so the loaded pages and scroll position are preserved. No-op if the
    // department isn't currently in the list.
    upsertDepartment(
      state: DepartmentsContainerState,
      action: PayloadAction<Department>
    ) {
      const updated = action.payload;
      if (!updated || !updated.id) return;
      const idx = state.collection.findIndex((d) => d.id === updated.id);
      if (idx !== -1) {
        state.collection[idx] = updated;
      }
    },
    setFilterValue(
      state: DepartmentsContainerState,
      action: PayloadAction<{ key: string; value: string }>
    ) {
      const { key, value } = action.payload;
      (state.filtersValue as any)[key] = value;
    },
  },
  /* eslint-enable */
});

const { actions, reducer } = departmentsContainerSlice;

export const {
  setDestroy,
  setLoading,
  setLoadingMore,
  setError,
  setData,
  appendData,
  upsertDepartment,
  setFilterValue,
} = actions;
export default reducer;

// Refresh just one already-loaded row after an inline edit — keeps loaded pages
// and scroll position (no page-1 reload).
export const refreshDepartment =
  (companyId: string, id: string) => async (dispatch: AppDispatch) => {
    try {
      const row = await fetchDepartmentRow(companyId, id);
      dispatch(upsertDepartment(row));
    } catch (e) {
      // Silent: the row simply keeps its previous values on a failed refresh.
    }
  };

// First page (replace). Called on mount, on filter change, on retry, and after
// create/edit to refresh the list.
export const fetchDepartmentsThunk =
  (
    companyId: string,
    filtersValue: { textSearch?: string } = {},
    page: number = 1,
    pageSize: number = DEPARTMENTS_PAGE_SIZE
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(setLoading(true));
    const { requestId } = getState().departmentsReducer;
    try {
      const data = await fetchDepartmentsPaged(companyId, filtersValue, {
        page,
        pageSize,
      });
      if (getState().departmentsReducer.requestId !== requestId) return;
      dispatch(setData(normalize(data)));
    } catch (e) {
      if (getState().departmentsReducer.requestId !== requestId) return;
      dispatch(setError({ error: "not_fetch" }));
    }
  };

// Subsequent pages (append) for infinite scroll.
export const fetchMoreDepartments =
  (
    companyId: string,
    filtersValue: { textSearch?: string },
    page: number,
    pageSize: number = DEPARTMENTS_PAGE_SIZE
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const { requestId } = getState().departmentsReducer;
    dispatch(setLoadingMore(true));
    try {
      const data = await fetchDepartmentsPaged(companyId, filtersValue, {
        page,
        pageSize,
      });
      if (getState().departmentsReducer.requestId !== requestId) return;
      dispatch(appendData(normalize(data)));
    } catch (e) {
      if (getState().departmentsReducer.requestId !== requestId) return;
      dispatch(setLoadingMore(false));
    }
  };
